import { useNavigate } from 'react-router-dom'
import { BankConvertor } from 'utilities/helper/banksConvertor'
import DotsLoading from 'components/loadings/dotsLoading'
import GuarantorPickerModal, {
    IGuarantorOption,
} from 'components/modals/guarantorPicker'
import useGetCards from 'pages/loans/add/hooks/useGetCards'
import { toman } from 'utilities/helper/format'
import { demandPriority } from 'utilities/helper/demandPriority'
import { IRequestTarget } from '../useRequestDraft'

/**
 * The last screen — everything the fund will read, plus the three optional
 * things that make it easier to say yes.
 *
 * The bank account is asked for here rather than up front because it is not a
 * decision, it is a detail: the member has usually already made up their mind
 * about the amount by the time they care where it lands. It stays required —
 * a request with nowhere to pay it is one a manager cannot act on.
 */
export default function StepReview({
    moneypoolId,
    target,
    isForSomeoneElse,
    amount,
    repayments,
    priority,
    note,
    onNote,
    guarantors,
    onGuarantors,
    cardId,
    onCard,
    showGuarantorPicker,
    onToggleGuarantorPicker,
}: {
    moneypoolId?: string
    target?: IRequestTarget
    isForSomeoneElse: boolean
    amount: string
    repayments: string
    priority: string
    note: string
    onNote: (next: string) => void
    guarantors: IGuarantorOption[]
    onGuarantors: (next: IGuarantorOption[]) => void
    cardId?: number
    onCard: (id: number) => void
    showGuarantorPicker: boolean
    onToggleGuarantorPicker: (open: boolean) => void
}) {
    const navigate = useNavigate()
    const { data, isLoading } = useGetCards(target?.poolship_id)
    const meta = demandPriority(priority)

    const count = Number(repayments) || 0
    const total = Number(amount) || 0
    const perInstallment = count > 0 ? Math.round(total / count) : 0

    // `receivers` is absent for a member with no registered account at all —
    // indexing into it unguarded is what took the grant wizard's review step
    // down with «باگ خوردیم» for exactly those people.
    const accounts = (data?.receivers ?? []).flatMap(
        (receiver: any) => receiver?.bankaccounts ?? []
    )

    return (
        <div>
            <div className=" rounded-2xl border border-stroke bg-white p-4">
                <div className=" flex items-baseline justify-between gap-2">
                    <p className=" text-lg font-bold text-heading">
                        {toman(total)}
                    </p>
                    <p className=" text-[11px] text-text-secondary">
                        {count.toLocaleString('fa-IR')} قسط ·{' '}
                        {perInstallment.toLocaleString('fa-IR')} تومان
                    </p>
                </div>
                {meta && (
                    <p className=" mt-2 text-[11px] text-text-secondary">
                        {meta.label} — {meta.need}
                    </p>
                )}
                {isForSomeoneElse && target && (
                    <p className=" mt-3 rounded-lg bg-canvas px-3 py-2 text-[11px] leading-6 text-text-body">
                        این درخواست به نام <b>{target.name}</b> ثبت می‌شود و
                        برای خودش پیامک می‌رود.
                    </p>
                )}
            </div>

            {/* توضیح — the promise the old form made and no manager screen
                kept: «این پیام فقط برای مدیران قابل مشاهده است». */}
            <div className=" mt-4 rounded-2xl border border-stroke bg-white p-4">
                <label
                    htmlFor="loan-request-note"
                    className=" block text-xs font-bold text-text-body"
                >
                    توضیح (اختیاری)
                </label>
                <textarea
                    id="loan-request-note"
                    value={note}
                    onChange={(event) => onNote(event.target.value)}
                    rows={3}
                    maxLength={1000}
                    placeholder="چرا به این وام نیاز داری؟"
                    className=" mt-2 w-full resize-none rounded-lg border border-stroke bg-canvas p-3 text-xs leading-6 outline-none focus:border-brand"
                />
                <p className=" mt-2 text-[11px] text-text-muted">
                    این توضیح فقط برای مدیران صندوق دیده می‌شود.
                </p>
            </div>

            {/* ضامن‌های پیشنهادی — a proposal, not a commitment. Nobody is
                told they were named until the loan actually exists. */}
            <div className=" mt-4 rounded-2xl border border-stroke bg-white p-4">
                <div className=" flex items-center justify-between gap-2">
                    <p className=" text-xs font-bold text-text-body">
                        ضامن‌های پیشنهادی (اختیاری)
                    </p>
                    <button
                        type="button"
                        onClick={() => onToggleGuarantorPicker(true)}
                        className=" text-[11px] font-bold text-green-1"
                    >
                        {guarantors.length ? 'تغییر' : 'انتخاب'}
                    </button>
                </div>
                {guarantors.length > 0 ? (
                    <ul className=" mt-2 flex flex-wrap gap-2">
                        {guarantors.map((item) => (
                            <li
                                key={item.poolship_id}
                                className=" rounded-full bg-canvas px-3 py-1 text-[11px] text-text-body"
                            >
                                {item.name}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className=" mt-2 text-[11px] text-text-muted">
                        هنوز کسی را پیشنهاد نداده‌ای. تا وام قطعی نشود به آن‌ها
                        اطلاعی داده نمی‌شود.
                    </p>
                )}
            </div>

            {/* شماره حساب */}
            <div className=" mt-4 rounded-2xl border border-stroke bg-white p-4">
                <p className=" text-xs font-bold text-text-body">
                    وام به کدام حساب واریز شود؟
                </p>

                {isLoading && <DotsLoading />}

                {!isLoading && accounts.length === 0 && (
                    <>
                        <p className=" mt-2 text-[11px] leading-6 text-text-muted">
                            {isForSomeoneElse
                                ? 'این عضو هنوز شماره حسابی ثبت نکرده است.'
                                : 'هنوز شماره حسابی ثبت نکرده‌ای.'}
                        </p>
                        {!isForSomeoneElse && (
                            <button
                                type="button"
                                onClick={() =>
                                    navigate('/manage-bank-cards/add')
                                }
                                className=" mt-3 w-full rounded-full border border-green-1 bg-white py-2 text-xs font-bold text-green-1"
                            >
                                ثبت شماره حساب جدید
                            </button>
                        )}
                    </>
                )}

                <ul className=" mt-2">
                    {accounts.map((bank: any) => (
                        <li
                            key={bank.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => onCard(bank.id)}
                            onKeyDown={(event) => {
                                if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                ) {
                                    event.preventDefault()
                                    onCard(bank.id)
                                }
                            }}
                            className={` mt-2 flex cursor-pointer items-center gap-3 rounded-lg border p-2 ${
                                cardId === bank.id
                                    ? ' border-green-1 bg-header-bg'
                                    : ' border-stroke bg-white'
                            }`}
                        >
                            <img
                                src={BankConvertor[bank.bank_unique_code]}
                                alt=""
                                className=" h-9 w-9 shrink-0"
                            />
                            <div className=" min-w-0">
                                <p className=" truncate text-[11px] font-bold text-text-body">
                                    {bank.full_name}
                                </p>
                                {/* dir=ltr: an account number is Latin digits
                                    and reverses inside an RTL paragraph */}
                                <p
                                    dir="ltr"
                                    className=" mt-1 text-right text-[11px] text-text-secondary"
                                >
                                    {(bank.card_number || bank.iban || '')
                                        .match(/.{1,4}/g)
                                        ?.join(' ')}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <GuarantorPickerModal
                show={showGuarantorPicker}
                onHide={() => onToggleGuarantorPicker(false)}
                moneypoolId={moneypoolId}
                value={guarantors}
                onChange={onGuarantors}
                excludePoolshipId={target?.poolship_id}
                title="ضامن‌های پیشنهادی"
            />
        </div>
    )
}
